"use client";

import React, { useEffect, useRef, useState } from "react";
import { Bug, Wind, Rat, BugOff, ShieldAlert, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  id: 1,
  title: "Port Charlotte Termite Treatments",
  description: "Protect your Port Charlotte home from destructive termites with our comprehensive inspection and targeted treatment plans. We also provide expert solutions for residents in ",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/port_charlotte_termite_treatment.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/port_charlotte_termite_treatment.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/port_charlotte_termite_treatment.png-wvc-sizes",
  linkTo: "/TermiteTreatmentPage",
  icon: Bug,
  badge: "Most Popular"
}, {
  id: 2,
  title: "Mosquito Control Services",
  description: "Reclaim your backyard. Our mosquito reduction services keep Port Charlotte families comfortable outdoors all year round. Our team is also active in ",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/mosquito_reduction_port_charlotte.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/mosquito_reduction_port_charlotte.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/mosquito_reduction_port_charlotte.png-wvc-sizes",
  linkTo: "/MosquitoControlPage",
  icon: Wind,
  badge: "Seasonal"
}, {
  id: 3,
  title: "Rodent Exclusion & Removal",
  description: "Keep rats and mice out of your property with our expert rodent control for Port Charlotte businesses and homes, ensuring a pest-free environment. Now serving ",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/rodent_exclusion_port_charlotte.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/rodent_exclusion_port_charlotte.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/rodent_exclusion_port_charlotte.png-wvc-sizes",
  linkTo: "/PestControlPage",
  icon: Rat,
  badge: ""
}, {
  id: 4,
  title: "Ant Eradication",
  description: "From ghost ants to fire ants, we eliminate colonies at the source to keep your Port Charlotte property completely ant-free and protected. Also available in ",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/ant_eradication_port_charlotte.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/ant_eradication_port_charlotte.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/ant_eradication_port_charlotte.png-wvc-sizes",
  linkTo: "/PestControlPage",
  icon: BugOff,
  badge: ""
}, {
  id: 5,
  title: "Cockroach Extermination",
  description: "Fast, effective roach control tailored for the Florida climate. Trusted by Port Charlotte families for a clean, healthy, and pest-free home. We also cover ",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cockroach_extermination_port_charlotte.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cockroach_extermination_port_charlotte.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cockroach_extermination_port_charlotte.png-wvc-sizes",
  linkTo: "/PestControlPage",
  icon: ShieldAlert,
  badge: "Essential"
}, {
  id: 6,
  title: "Bug Management",
  description: "Reduce spider populations around your home's exterior and interior with our targeted Port Charlotte pest management solutions for year-round peace of mind. Serving ",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/spider_management_port_charlotte.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/spider_management_port_charlotte.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/spider_management_port_charlotte.png-wvc-sizes",
  linkTo: "/PestControlPage",
  icon: Bug,
  badge: ""
}];
export default function PortCharlottePestControlServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="port-charlotte-pest-control-services" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
            <div className="container mx-auto px-4 md:px-6">
                <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <Badge variant="secondary" className="mb-4 font-default tracking-wider uppercase">
                        Local Expertise
                    </Badge>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4">
                        Trusted Pest Control in Port Charlotte
                    </h2>
                    <p className="text-lg text-foreground/80 font-serif">
                        We provide comprehensive, climate-specific pest management solutions designed to protect Port Charlotte homes and businesses from Florida's toughest pests.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {services.map((service, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          const Icon = service.icon;
          return <div key={service.id} data-index={i} ref={cardRef} className={`transition-all duration-700 ease-out ${isCardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                                <Card className="h-full flex flex-col overflow-hidden border-border/20 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-card group">
                                    <div className="relative overflow-hidden">
                                        <AspectRatio ratio={16 / 9}>
                                            <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                                        </AspectRatio>
                                        {service.badge && <div className="absolute top-3 right-3 z-10">
                                                <Badge variant="default" className="font-default shadow-sm">
                                                    {service.badge}
                                                </Badge>
                                            </div>}
                                        <div className="absolute -bottom-6 left-6 bg-primary text-primary-foreground p-3 rounded-lg shadow-md z-10">
                                            <Icon className="size-6" />
                                        </div>
                                    </div>
                                    
                                    <CardHeader className="pt-10 pb-4">
                                        <CardTitle className="text-xl md:text-2xl font-default text-card-foreground line-clamp-2">
                                            {service.title}
                                        </CardTitle>
                                    </CardHeader>
                                    
                                    <CardContent className="flex-grow">
                                        <CardDescription className="text-base font-serif text-muted-foreground">
                                            {service.description}
                                            <Link to="/CapeCoralPestControlPage" className="text-primary hover:underline font-bold">
                                                Cape Coral
                                            </Link>.
                                            <div className="mt-2">
                                                {service.id === 1 && <Link to="/TermiteTreatmentPage" className="text-primary hover:underline font-bold">
                                                        Port Charlotte termite treatment options
                                                    </Link>}
                                                {service.id === 2 && <Link to="/MosquitoControlPage" className="text-primary hover:underline font-bold">
                                                        Port Charlotte mosquito control plans
                                                    </Link>}
                                            </div>
                                        </CardDescription>
                                    </CardContent>
                                    
                                    <CardFooter className="pt-4 pb-6 border-t border-border/10 mt-auto">
                                        <Button asChild variant="ghost" className="w-full justify-between group/btn hover:bg-accent hover:text-accent-foreground font-default">
                                            <Link to={service.linkTo}>
                                                <span>Learn More</span>
                                                <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                                            </Link>
                                        </Button>
                                    </CardFooter>
                                </Card>
                            </div>;
        })}
                </div>

                <div className="mt-12 flex flex-wrap justify-center gap-4 text-center">
                    <p className="w-full text-muted-foreground font-serif mb-2">Also providing expert pest solutions in neighboring communities:</p>
                    <Button asChild variant="outline" className="rounded-lg">
                        <Link to="/PuntaGordaPestControlPage">Punta Gorda Pest Services</Link>
                    </Button>
                    <Button asChild variant="outline" className="rounded-lg">
                        <Link to="/NorthFortMyersPestControlPage">North Fort Myers Exterminators</Link>
                    </Button>
                    <Button asChild variant="outline" className="rounded-lg">
                        <Link to="/CapeCoralPestControlPage">Cape Coral Pest Management</Link>
                    </Button>
                </div>
                
                <div className="mt-12 md:mt-16 text-center">
                    <Button asChild size="lg" className="font-default text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all">
                        <Link to="/ContactPage">
                            Get a Free Port Charlotte Quote
                        </Link>
                    </Button>
                </div>
            </div>
        </section>;
}